/**
 * Card combat engine — ADR-0015 §11 turn-by-turn loop (HEX-52).
 *
 * Replaces the V1 engine (`engine.ts`, retained until the HEX-52/HEX-53 cutover
 * sweep) with the deck-builder reframe: two symmetric `Combatant`s (player +
 * enemy), each with a loadout grid of cards and a per-turn rolled dice pool.
 *
 * The loop is alternating, not simultaneous. The player takes the first turn;
 * turns alternate until one side reaches 0 HP. Per turn (active side):
 *
 *   1. roll       — active pool = basePoolSize + equipped-die-cards d6
 *   2. (visibility — UI concern; both loadouts/bags always visible)
 *   3. assign + resolve — active assigns dice to loadout cards; cards resolve in
 *      row-major placement order; `rollFresh` sub-rolls resolve inline off the
 *      engine RNG (depth-1 capped)
 *   4. reactive trap firing — opponent-grid traps matching the active side's
 *      actions fire immediately, in opponent row-major order (depth-1: a trap
 *      cannot trigger another trap)
 *   5. tick — active side's status bag ticks (tick → expire → drain pending)
 *   6. death check — first combatant to reach 0 HP loses at that instant,
 *      including self-damage on the active side's own turn
 *
 * RNG (ADR-0008 §9): the single randomness source. State is pure data — the RNG
 * is replayed from `seed` + `rngStepCount` (mulberry32, `createRng`), so
 * identical inputs produce identical state sequences. Both pools and every
 * `rollFresh` draw the same underlying stream.
 *
 * Carried substrate (ADR-0015 §15): HEX-19 (`matches`, dice), HEX-20 (status
 * `apply`/`tick`/`expire`/`query`, overwrite-on-reapply), ADR-0008 §6 block
 * discard-then-earn, the 2026-05-21 turn-end order (tick → expire → drain).
 *
 * The public event/state surface (consumed by HEX-53) is documented in
 * `docs/combat-engine-surface.md`.
 */

import { type DieType, PLAIN_D6, createRng, rollDie } from './dice';
import { chooseEnemyAssignment } from './enemyAi';
import { matches } from './predicates';
import { type StatusBag, apply as applyStatus, expire, query, tick } from './status';
import type {
  AmountSource,
  Card,
  CardEffect,
  CardId,
  Combatant,
  DieFace,
  EffectTarget,
  GridPlacement,
  PerFaceEffect,
  Predicate,
  StatusApplication,
  TrapCondition,
  TrapRuntime,
} from './types';

// --------------------------------------------------------------------------
// Public types
// --------------------------------------------------------------------------

export type Side = 'player' | 'enemy';

/** `assign` — the active side is building its turn (player assigns via intents;
 * the enemy auto-assigns when its turn resolves). `ended` — combat is over. */
export type Phase = 'assign' | 'ended';

/** A rolled die plus its assignment state for the active side's pool. */
export type DieSlot = {
  index: number;
  face: DieFace;
  /** Card this die is committed to, or null if free. */
  assignedTo: CardId | null;
};

/** The dice the active side has committed to one of its loadout cards. For
 * normal cards these satisfy `card.cost`; for loaded/charged traps they are the
 * feed dice that arm / charge the trap. */
export type CardAssignment = {
  cardId: CardId;
  dieIndices: number[];
};

/** Resolution / lifecycle events, appended to `state.log` in resolution order.
 * HEX-53 reads the log (diff by length) to drive the combat-log band and the
 * hand-rolled animation moments. */
export type CombatEvent =
  | { kind: 'turnStart'; side: Side; turn: number; faces: DieFace[] }
  | { kind: 'cardPlayed'; side: Side; cardId: CardId; cardName: string }
  | { kind: 'damage'; source: Side; target: Side; amount: number; blocked: number; hpAfter: number }
  | { kind: 'block'; side: Side; amount: number; total: number }
  | { kind: 'statusApplied'; target: Side; status: StatusApplication }
  | { kind: 'reroll'; side: Side; count: number }
  // HEX-88: `fizzled` true when no `perFace` branch matched any rolled face and
  // there was no `onSum` block — the recursive roll resolved to no effect. The
  // overlay surfaces this as a "fizzles — no effect" log line + subtle cue.
  | { kind: 'rollFresh'; side: Side; faces: DieFace[]; fizzled: boolean }
  | { kind: 'trapArmed'; side: Side; cardId: CardId }
  | { kind: 'trapCharged'; side: Side; cardId: CardId; charges: number }
  | { kind: 'trapFired'; side: Side; cardId: CardId; cardName: string }
  | { kind: 'cardFrozen'; side: Side; cardId: CardId; cardName: string }
  | { kind: 'poisonTick'; side: Side; amount: number; hpAfter: number }
  // HEX-89: a `heal` effect restored HP (clamped at maxHp). `amount` is the HP
  // actually gained (0 when already capped).
  | { kind: 'heal'; side: Side; amount: number; hpAfter: number }
  | { kind: 'turnEnd'; side: Side }
  // ADR-0015 §19 retreat roll. `escaped` true → clean exit (odd face); false →
  // the next player turn is skipped (even face).
  | { kind: 'retreatRolled'; face: DieFace; escaped: boolean }
  // ADR-0015 §19: a player turn auto-consumed by a prior failed retreat.
  | { kind: 'playerTurnSkipped'; turn: number }
  | { kind: 'combatEnded'; victor: Side };

export type CardCombatState = {
  seed: number;
  rngStepCount: number;
  turn: number;
  activeSide: Side;
  phase: Phase;
  player: Combatant;
  enemy: Combatant;
  /** The active side's rolled pool with assignment state. */
  pool: DieSlot[];
  /** The active side's current card assignments (cleared at each turn start). */
  assignments: CardAssignment[];
  /** ADR-0015 §19: a failed retreat (even roll) sets this; the next would-be
   * player turn is auto-consumed (rolled + resolved with no assignments) and the
   * flag clears. */
  skipNextPlayerTurn: boolean;
  /** ADR-0015 §19: set to `'clean'` when a retreat roll succeeds (odd face); the
   * overlay reads it and reports a forfeit-style exit. Null otherwise. */
  retreat: 'clean' | null;
  victor: Side | null;
  log: CombatEvent[];
};

export type CardEngineIntent =
  | { kind: 'assignDie'; dieIndex: number; cardId: CardId }
  /** Release a single committed die back to the pool (HEX-59). Player-only; the
   * card keeps any other dice it holds. `clearCard` releases the whole set. */
  | { kind: 'unassignDie'; dieIndex: number }
  | { kind: 'clearCard'; cardId: CardId }
  /** Activate a free (cost-less, non-trap) card immediately during the assign
   * phase (ADR-0015 §19; Reroll). `dieIndex` is the player's pick for a
   * `rerollDice` effect; omit to reroll the lowest free die. Player-only. */
  | { kind: 'activateCard'; cardId: CardId; dieIndex?: number }
  /** Attempt a retreat (ADR-0015 §19). Draws one engine die: odd → clean exit
   * (`state.retreat = 'clean'`); even → `skipNextPlayerTurn` set and the current
   * turn ends. Player-only. */
  | { kind: 'attemptRetreat' }
  | { kind: 'endTurn' }
  /** Resolve the enemy turn (auto-assign placeholder AI). Valid only when
   * `activeSide === 'enemy'`. The interactive intents above are player-only. */
  | { kind: 'runEnemyTurn' };

// --------------------------------------------------------------------------
// Die-card detection (ADR-0015 §2)
// --------------------------------------------------------------------------

/**
 * A die card is pure pool fuel: a 1×1 card with no cost, no effects and no trap
 * behaviour — equipping it adds one die to the owner's pool and nothing else
 * (ADR-0015 §2). The discriminant is the explicit `type === 'die'` (ADR-0015
 * §17), which replaced the earlier structural inference.
 */
export function isDieCard(card: Card): boolean {
  return card.type === 'die';
}

/** The `die`-card placements packed in the loadout, in row-major placement order
 * (ADR-0024 §2). Each occupies one cell and adds its `dieType` (a specialist die)
 * — or a `PLAIN_D6` for the plain pool-fuel `extra-die` — to the roll pool. */
function packedDieCards(combatant: Combatant): DieType[] {
  const dice: DieType[] = [];
  for (const placement of combatant.inventory.loadout.placements) {
    if (isDieCard(placement.card)) dice.push(placement.card.dieType ?? PLAIN_D6);
  }
  return dice;
}

/** `basePoolSize + count(packed die cards)` (ADR-0015 §2; ADR-0024 §1, §3). The
 * free baseline plus every specialist die occupying a loadout cell. A combatant
 * with no packed dice rolls exactly its baseline — the roll floor (ADR-0024 §1). */
export function poolSize(combatant: Combatant): number {
  return combatant.basePoolSize + packedDieCards(combatant).length;
}

/** The typed dice that make up this combatant's pool, in roll order (ADR-0024
 * §3). `poolDice(c).length === poolSize(c)`: the implicit `basePoolSize` baseline
 * `PLAIN_D6`, then each `die` card packed in the loadout (its `dieType`, or a
 * `PLAIN_D6` for plain pool-fuel). Enemies / fixtures with no packed dice yield an
 * all-plain-d6 pool → byte-identical to the historic fixed-d6 roll. */
export function poolDice(combatant: Combatant): DieType[] {
  return [...plainDice(combatant.basePoolSize), ...packedDieCards(combatant)];
}

/** An array of `n` plain d6 — the roll source for die cards, fresh rolls and any
 * combatant without owned dice. */
function plainDice(n: number): DieType[] {
  return Array.from({ length: n }, () => PLAIN_D6);
}

// --------------------------------------------------------------------------
// Public API
// --------------------------------------------------------------------------

export function createCombat(args: {
  player: Combatant;
  enemy: Combatant;
  seed: number;
}): CardCombatState {
  const base: CardCombatState = {
    seed: args.seed,
    rngStepCount: 0,
    turn: 1,
    activeSide: 'player',
    phase: 'assign',
    player: cloneCombatant(args.player),
    enemy: cloneCombatant(args.enemy),
    pool: [],
    assignments: [],
    skipNextPlayerTurn: false,
    retreat: null,
    victor: null,
    log: [],
  };
  return rollForActive(base);
}

export function applyIntent(state: CardCombatState, intent: CardEngineIntent): CardCombatState {
  if (state.phase === 'ended') return state;
  switch (intent.kind) {
    case 'assignDie':
      requirePlayerTurn(state, intent.kind);
      return doAssign(state, intent.dieIndex, intent.cardId);
    case 'unassignDie':
      requirePlayerTurn(state, intent.kind);
      return doUnassign(state, intent.dieIndex);
    case 'clearCard':
      requirePlayerTurn(state, intent.kind);
      return doClear(state, intent.cardId);
    case 'activateCard':
      requirePlayerTurn(state, intent.kind);
      return doActivateCard(state, intent.cardId, intent.dieIndex);
    case 'attemptRetreat':
      requirePlayerTurn(state, intent.kind);
      return doAttemptRetreat(state);
    case 'endTurn':
      requirePlayerTurn(state, intent.kind);
      return resolveTurn(state);
    case 'runEnemyTurn':
      if (state.activeSide !== 'enemy') {
        throw new Error(`'runEnemyTurn' valid only on the enemy turn`);
      }
      return resolveTurn(applyEnemyAssignment(state));
  }
}

function requirePlayerTurn(state: CardCombatState, kind: string): void {
  if (state.activeSide !== 'player') {
    throw new Error(`'${kind}' valid only on the player turn`);
  }
}

// --------------------------------------------------------------------------
// Assignment (player)
// --------------------------------------------------------------------------

function doAssign(state: CardCombatState, dieIndex: number, cardId: CardId): CardCombatState {
  const die = state.pool[dieIndex];
  if (die === undefined) throw new Error(`invalid die index ${dieIndex}`);
  if (die.assignedTo !== null) throw new Error(`die ${dieIndex} already assigned`);

  const active = activeCombatant(state);
  const placement = findLoadout(active, cardId);
  if (placement === undefined) throw new Error(`card '${cardId}' not in active loadout`);
  if ((active.frozenCards.get(cardId) ?? 0) > 0) {
    throw new Error(`card '${cardId}' is frozen and cannot receive a die`);
  }
  const card = placement.card;
  const feed = feedCost(card);
  if (feed.length === 0) {
    throw new Error(`card '${cardId}' takes no dice (passive trap, die card, or activated card)`);
  }

  const existing = state.assignments.find((a) => a.cardId === cardId);
  const currentFaces = (existing?.dieIndices ?? []).map((i) => slotFace(state, i));
  const nextFaces = [...currentFaces, die.face];
  // §19 scaling: a card with `extraCost` accepts dice past `cost.length` (up to
  // the whole pool); without it the cap is the cost slots.
  const cap = card.extraCost ? state.pool.length : feed.length;
  if (nextFaces.length > cap) {
    throw new Error(`card '${cardId}' is already fully fed`);
  }
  if (!assignmentValid(nextFaces, card)) {
    throw new Error(`die face ${die.face} does not satisfy any open cost slot of '${cardId}'`);
  }

  const pool = state.pool.map((s) => (s.index === dieIndex ? { ...s, assignedTo: cardId } : s));
  const assignments = existing
    ? state.assignments.map((a) =>
        a.cardId === cardId ? { ...a, dieIndices: [...a.dieIndices, dieIndex] } : a,
      )
    : [...state.assignments, { cardId, dieIndices: [dieIndex] }];
  return { ...state, pool, assignments };
}

/**
 * Release a single committed die back to the pool (HEX-59). Frees only
 * `dieIndex`; the card it fed keeps its other dice. If that was the card's last
 * die, the now-empty assignment is dropped. Unassignment is only reachable on
 * the player's assign phase (same window as `assignDie` / `clearCard`), so it
 * can never race a trap resolution — dice are committed and cleared atomically
 * inside `resolveTurn`, long after the assign phase closes.
 */
function doUnassign(state: CardCombatState, dieIndex: number): CardCombatState {
  const die = state.pool[dieIndex];
  if (die === undefined) throw new Error(`invalid die index ${dieIndex}`);
  if (die.assignedTo === null) throw new Error(`die ${dieIndex} is not assigned`);
  const cardId = die.assignedTo;
  const pool = state.pool.map((s) => (s.index === dieIndex ? { ...s, assignedTo: null } : s));
  const assignments = state.assignments
    .map((a) =>
      a.cardId === cardId ? { ...a, dieIndices: a.dieIndices.filter((i) => i !== dieIndex) } : a,
    )
    .filter((a) => a.dieIndices.length > 0);
  return { ...state, pool, assignments };
}

function doClear(state: CardCombatState, cardId: CardId): CardCombatState {
  const assignment = state.assignments.find((a) => a.cardId === cardId);
  if (assignment === undefined) throw new Error(`no assignment for card '${cardId}'`);
  const freed = new Set(assignment.dieIndices);
  const pool = state.pool.map((s) => (freed.has(s.index) ? { ...s, assignedTo: null } : s));
  const assignments = state.assignments.filter((a) => a.cardId !== cardId);
  return { ...state, pool, assignments };
}

/**
 * Activate a free (cost-less, non-trap, non-die) card immediately during the
 * assign phase (ADR-0015 §19; Reroll). Its effects resolve at once against the
 * live pool — a `rerollDice` rerolls the player's chosen die (`dieIndex`) — so
 * the rescue lands before assignment continues. Gated by `oncePerTurn` (already
 * fired this turn → rejected) and `frozenCards`.
 */
function doActivateCard(
  state: CardCombatState,
  cardId: CardId,
  dieIndex: number | undefined,
): CardCombatState {
  const active = activeCombatant(state);
  const placement = findLoadout(active, cardId);
  if (placement === undefined) throw new Error(`card '${cardId}' not in active loadout`);
  const card = placement.card;
  if (card.type === 'trap' || card.type === 'die' || card.cost.length > 0) {
    throw new Error(`card '${cardId}' is not an activated card`);
  }
  if ((active.frozenCards.get(cardId) ?? 0) > 0) {
    throw new Error(`card '${cardId}' is frozen and cannot be activated`);
  }
  if (card.oncePerTurn === true && active.oncePerTurnFired.has(cardId)) {
    throw new Error(`card '${cardId}' already activated this turn`);
  }
  if (card.oncePerCombat === true && active.oncePerCombatFired.has(cardId)) {
    throw new Error(`card '${cardId}' already activated this combat`);
  }
  const ctx: Ctx = {
    state,
    active: state.activeSide,
    inTrap: false,
    trapFires: new Map(),
    ...(dieIndex !== undefined ? { rerollTarget: dieIndex } : {}),
  };
  log(ctx, { kind: 'cardPlayed', side: ctx.active, cardId, cardName: card.name });
  if (card.oncePerTurn === true) markFired(ctx, ctx.active, 'oncePerTurnFired', cardId);
  if (card.oncePerCombat === true) markFired(ctx, ctx.active, 'oncePerCombatFired', cardId);
  resolveEffects(ctx, card.effects, { source: ctx.active, allowRollFresh: true });
  return ctx.state;
}

/**
 * Attempt a retreat (ADR-0015 §19). Draws one die off the engine stream: an odd
 * face is a clean exit (`retreat = 'clean'`; the overlay forfeits); an even face
 * stays in combat, sets `skipNextPlayerTurn`, and ends the current player turn
 * (the cost of a failed escape). Replayable under seed like every other draw.
 */
function doAttemptRetreat(state: CardCombatState): CardCombatState {
  const ctx: Ctx = { state, active: state.activeSide, inTrap: false, trapFires: new Map() };
  const [face] = drawFresh(ctx, 1);
  const roll = (face ?? 1) as DieFace;
  const escaped = roll % 2 === 1;
  log(ctx, { kind: 'retreatRolled', face: roll, escaped });
  if (escaped) {
    return { ...ctx.state, retreat: 'clean' };
  }
  // Failed: flag the skip, discard any committed dice, end the turn now.
  const failed: CardCombatState = {
    ...ctx.state,
    skipNextPlayerTurn: true,
    assignments: [],
    pool: ctx.state.pool.map((s) => ({ ...s, assignedTo: null })),
  };
  return resolveTurn(failed);
}

/** Whether `cardId` has a full, valid set of dice committed (ready to resolve). */
export function cardReady(state: CardCombatState, cardId: CardId): boolean {
  const placement = findLoadout(activeCombatant(state), cardId);
  if (placement === undefined) return false;
  const faces = assignedFaces(state, placement.card.id);
  return facesReady(faces, placement.card);
}

/** The faces committed to `cardId` in the active side's assignments, in order. */
function assignedFaces(state: CardCombatState, cardId: CardId): DieFace[] {
  const assignment = state.assignments.find((a) => a.cardId === cardId);
  return (assignment?.dieIndices ?? []).map((i) => slotFace(state, i));
}

/** Whether `faces` is enough to play `card`: at least the cost slots filled and a
 * valid cost(+extra) matching exists (ADR-0015 §19). Extra dice past `cost.length`
 * are optional, so readiness keys off the cost floor, not an exact count. */
function facesReady(faces: readonly DieFace[], card: Card): boolean {
  if (faces.length < card.cost.length) return false;
  return assignmentValid(faces, card);
}

// --------------------------------------------------------------------------
// Turn resolution (§11)
// --------------------------------------------------------------------------

type Ctx = {
  state: CardCombatState;
  active: Side;
  /** Depth guard — true while resolving a trap's effects, suppressing further
   * reactive trap firing (ADR-0015 §11 depth-1 cap). */
  inTrap: boolean;
  /** Per-turn fire count per trap card id, for `trap.maxFiresPerTurn` (ADR-0015
   * §17). Ephemeral — recreated each turn, never serialised. */
  trapFires: Map<CardId, number>;
  /** The player's chosen die index for an activated `rerollDice` (ADR-0015 §19
   * Reroll). Set only on the `activateCard` pass; undefined elsewhere. */
  rerollTarget?: number;
};

/** Per-effect-run context (ADR-0015 §17). Carries the resolving `source`, the
 * depth-1 `rollFresh` gate, the fresh-roll magnitude scope (`faceValue` inside a
 * `perFace` branch, `faceSum` inside an `onSum` block) and the triggering
 * `TrapTrigger` while a trap's own effects resolve. */
type EffectCtx = {
  source: Side;
  allowRollFresh: boolean;
  faceValue?: number;
  faceSum?: number;
  trigger?: TrapTrigger;
};

/** The action that set a trap off (ADR-0015 §17). `attacker` is the side whose
 * turn produced the trigger; the magnitudes feed the trap-context effects
 * (`reflectDamage` reads `damageToOwner`, `reduceBlock` reads `blockGained`,
 * `mirrorStatus` reads `statuses`). */
type TrapTrigger = {
  attacker: Side;
  damageToOwner: number;
  blockGained: number;
  statuses: StatusApplication[];
};

function emptyTrigger(attacker: Side): TrapTrigger {
  return { attacker, damageToOwner: 0, blockGained: 0, statuses: [] };
}

function resolveTurn(state: CardCombatState): CardCombatState {
  const active = state.activeSide;
  const ctx: Ctx = { state, active, inTrap: false, trapFires: new Map() };

  // §11.3 — block discard-then-earn: active side's block resets before its
  // defense cards re-earn it this turn (ADR-0008 §6).
  setBlock(ctx, active, 0);

  // Roll-face traps (`opponentRollFace`) already fired at roll time in
  // `rollForActive`, before the active side assigned (so a Cold Snap freeze can
  // bar the frozen card from receiving a die this turn — ADR-0015 §14, §17).

  // §11.3 — resolve active loadout cards in row-major placement order.
  const order = rowMajor(combatantOf(ctx.state, active).inventory.loadout.placements);
  for (const placement of order) {
    if (ended(ctx)) break;
    resolvePlacement(ctx, placement);
  }

  // §11.4 (turn-end traps) — `turnEnd` traps on both loadouts fire at the end of
  // every turn (active side's first, then opponent's), in row-major order.
  if (!ended(ctx)) fireTurnEndTraps(ctx);

  // §11.5 — active side's status bag ticks (tick → expire → drain pending).
  if (!ended(ctx)) tickActive(ctx);

  if (ended(ctx)) return ctx.state;
  log(ctx, { kind: 'turnEnd', side: active });

  // Pass the turn to the opponent and roll their fresh pool (§11 → step 1).
  const next: CardCombatState = {
    ...ctx.state,
    activeSide: other(active),
    turn: ctx.state.turn + 1,
    assignments: [],
    pool: [],
  };
  const rolled = rollForActive(next);

  // §19 retreat skip: a failed retreat queued `skipNextPlayerTurn`. When it next
  // becomes the player's turn, the roll has already happened (turn-start traps
  // fired) — auto-consume the turn by resolving with no assignments, handing
  // straight back to the enemy. The flag clears so it fires once.
  if (rolled.phase !== 'ended' && rolled.activeSide === 'player' && rolled.skipNextPlayerTurn) {
    const cleared: CardCombatState = {
      ...rolled,
      skipNextPlayerTurn: false,
      log: [...rolled.log, { kind: 'playerTurnSkipped', turn: rolled.turn }],
    };
    return resolveTurn(cleared);
  }
  return rolled;
}

function resolvePlacement(ctx: Ctx, placement: GridPlacement): void {
  const card = placement.card;
  if (isDieCard(card)) return; // pure pool fuel; nothing to resolve

  // Loaded / charged traps that were fed this turn arm / charge now; their
  // trigger effects fire reactively, not here (ADR-0015 §3).
  if (card.type === 'trap') {
    feedTrapIfAssigned(ctx, card);
    return;
  }

  // Free (cost-less, non-trap) cards are player-activated during the assign phase
  // (ADR-0015 §19; Reroll) — they never auto-resolve at turn resolution.
  if (card.cost.length === 0) return;

  if (!isAssignmentReady(ctx.state, card)) return; // not played this turn
  if (
    card.oncePerCombat === true &&
    combatantOf(ctx.state, ctx.active).oncePerCombatFired.has(card.id)
  ) {
    return; // already spent this combat
  }
  if (
    card.oncePerTurn === true &&
    combatantOf(ctx.state, ctx.active).oncePerTurnFired.has(card.id)
  ) {
    return; // already fired this turn (§19)
  }

  log(ctx, { kind: 'cardPlayed', side: ctx.active, cardId: card.id, cardName: card.name });
  if (card.oncePerCombat === true) markFired(ctx, ctx.active, 'oncePerCombatFired', card.id);
  if (card.oncePerTurn === true) markFired(ctx, ctx.active, 'oncePerTurnFired', card.id);

  const ectx: EffectCtx = { source: ctx.active, allowRollFresh: true };
  const tags = resolveEffects(ctx, card.effects, ectx);

  // §19 scaling + cost-face bonuses, on top of the base effects. The committed
  // dice split into cost dice (drive `costBonuses`) and extra dice (drive
  // `extraCost.perDie`); only cost dice trigger bonuses (§19 default).
  if (!ended(ctx) && (card.extraCost !== undefined || card.costBonuses !== undefined)) {
    const { costFaces, extraFaces } = splitCostExtra(assignedFaces(ctx.state, card.id), card);
    if (card.extraCost !== undefined) {
      for (let i = 0; i < extraFaces.length && !ended(ctx); i++) {
        mergeTags(tags, resolveEffects(ctx, card.extraCost.perDie, ectx));
      }
    }
    if (card.costBonuses !== undefined) {
      for (const face of costFaces) {
        if (ended(ctx)) break;
        for (const bonus of card.costBonuses) {
          if (ended(ctx)) break;
          if (matches(face, bonus.facePredicate)) {
            mergeTags(tags, resolveEffects(ctx, bonus.effects, ectx));
          }
        }
      }
    }
  }
  if (ended(ctx)) return;

  // §11.4 — opponent's reactive traps fire on the action the card just took.
  fireReactiveTraps(ctx, tags);
}

/** Merge `extra`'s action tags into `into` (ADR-0015 §19 — scaling / bonus effect
 * runs contribute to the same reactive-trap trigger as the base effects). */
function mergeTags(into: ActionTags, extra: ActionTags): void {
  into.attacked = into.attacked || extra.attacked;
  into.blocked = into.blocked || extra.blocked;
  into.damageToOpponent += extra.damageToOpponent;
  into.blockGained += extra.blockGained;
  into.statuses.push(...extra.statuses);
}

/** Add `cardId` to one of the per-card fired sets, cloning it first (pure
 * mutation discipline). */
function markFired(
  ctx: Ctx,
  side: Side,
  field: 'oncePerCombatFired' | 'oncePerTurnFired',
  cardId: CardId,
): void {
  const fired = new Set(combatantOf(ctx.state, side)[field]);
  fired.add(cardId);
  mutate(ctx, side, { [field]: fired });
}

/** What an effect run did, used to drive reactive trap conditions and the
 * trap-context effects (ADR-0015 §17). `damageToOpponent` / `blockGained` carry
 * the magnitudes `reflectDamage` / `reduceBlock` read back. */
type ActionTags = {
  attacked: boolean;
  blocked: boolean;
  statuses: StatusApplication[];
  damageToOpponent: number;
  blockGained: number;
};

/**
 * Resolve a list of card effects for `ectx.source`, mutating combatant state
 * through `ctx`. Returns the action tags raised. `ectx.allowRollFresh` is true
 * for top-level / trap-trigger effects and false inside a `rollFresh` branch
 * (depth-1 cap, ADR-0015 §11).
 */
function resolveEffects(ctx: Ctx, effects: readonly CardEffect[], ectx: EffectCtx): ActionTags {
  const tags: ActionTags = {
    attacked: false,
    blocked: false,
    statuses: [],
    damageToOpponent: 0,
    blockGained: 0,
  };
  const source = ectx.source;
  for (const effect of effects) {
    if (ended(ctx)) break;
    switch (effect.kind) {
      case 'damage': {
        const target = resolveTarget(ctx, ectx, effect.target, 'opponent');
        const amount = resolveAmount(ectx, effect.amount, effect.amountFrom);
        // Self-targeted damage is raw HP loss — no weaken scaling, no block.
        const dealt = applyDamage(ctx, source, target, amount, { raw: target === source });
        if (target === other(source)) {
          tags.attacked = true;
          tags.damageToOpponent += dealt;
        }
        break;
      }
      case 'block': {
        const target = resolveTarget(ctx, ectx, effect.target, 'self');
        const amount = resolveAmount(ectx, effect.amount, effect.amountFrom);
        const total = addBlock(ctx, target, amount);
        log(ctx, { kind: 'block', side: target, amount, total });
        if (target === source) {
          tags.blocked = true;
          tags.blockGained += amount;
        }
        break;
      }
      case 'applyStatus': {
        // §17 `target` overrides; legacy data carries the side in
        // `application.target`, so fall back to it for backward compatibility.
        const target = resolveTarget(
          ctx,
          ectx,
          effect.target ?? effect.application.target,
          'opponent',
        );
        const magnitude = resolveAmount(ectx, effect.application.magnitude, effect.magnitudeFrom);
        const app: StatusApplication = { ...effect.application, magnitude };
        applyStatusToSide(ctx, target, app);
        if (target === other(source)) tags.statuses.push(app);
        break;
      }
      case 'rerollDice': {
        doRerollDice(ctx, source, effect.count);
        break;
      }
      case 'heal': {
        // Restore HP to `target` (default self), clamped at maxHp (HEX-89).
        // Symmetric — fires for either side, no block / status interaction.
        const target = resolveTarget(ctx, ectx, effect.target, 'self');
        applyHeal(ctx, target, effect.amount);
        break;
      }
      case 'rollFresh': {
        if (!ectx.allowRollFresh) {
          throw new Error('rollFresh exceeds depth-1 cap (nested rollFresh forbidden)');
        }
        // §11.4 — the recursive sub-effects' tags fold into this run so the
        // rollFresh damage/statuses fire the same reactive traps a plain attack
        // would (HEX-87: dropping these tags was the "recursives don't trigger"
        // bug).
        mergeTags(tags, resolveRollFresh(ctx, ectx, effect.count, effect.perFace, effect.onSum));
        break;
      }
      case 'armOrCharge':
        // Trap feed effect; processed by feedTrapIfAssigned, not on play.
        break;
      case 'reflectDamage': {
        const trig = requireTrigger(ectx, 'reflectDamage');
        const amount = Math.floor(effect.fraction * trig.damageToOwner);
        if (amount > 0) applyDamage(ctx, source, trig.attacker, amount, { raw: true });
        break;
      }
      case 'reduceBlock': {
        const trig = requireTrigger(ectx, 'reduceBlock');
        const victim = combatantOf(ctx.state, trig.attacker);
        mutate(ctx, trig.attacker, { block: Math.max(0, victim.block - effect.amount) });
        break;
      }
      case 'mirrorStatus': {
        const trig = requireTrigger(ectx, 'mirrorStatus');
        for (const app of trig.statuses) {
          if (ended(ctx)) break;
          applyStatusToSide(ctx, trig.attacker, app);
        }
        break;
      }
      case 'freezeCard': {
        const trig = requireTrigger(ectx, 'freezeCard');
        freezeSelectedCard(ctx, trig.attacker, effect.select);
        break;
      }
    }
  }
  return tags;
}

/** Trap-context effects throw if resolved outside a trap firing (ADR-0015 §17). */
function requireTrigger(ectx: EffectCtx, kind: string): TrapTrigger {
  if (ectx.trigger === undefined) {
    throw new Error(`'${kind}' is valid only while a trap is firing`);
  }
  return ectx.trigger;
}

/** Resolve the magnitude of a face-derived effect (ADR-0015 §17). `faceValue`
 * is valid only inside a `perFace` branch, `faceSum` only inside `onSum`; the
 * engine throws otherwise. With no source, the literal `base` is used. */
function resolveAmount(
  ectx: EffectCtx,
  base: number,
  amountFrom: AmountSource | undefined,
): number {
  if (amountFrom === undefined) return base;
  if (amountFrom === 'faceValue') {
    if (ectx.faceValue === undefined) {
      throw new Error(
        'amountFrom/magnitudeFrom faceValue is valid only inside a rollFresh perFace branch',
      );
    }
    return ectx.faceValue;
  }
  if (ectx.faceSum === undefined) {
    throw new Error(
      'amountFrom/magnitudeFrom faceSum is valid only inside a rollFresh onSum block',
    );
  }
  return ectx.faceSum;
}

/** Resolve an `EffectTarget` to a concrete side. `random` is a 50/50 d6 coin off
 * the engine stream (even → opponent, odd → self). */
function resolveTarget(
  ctx: Ctx,
  ectx: EffectCtx,
  target: EffectTarget | undefined,
  fallback: EffectTarget,
): Side {
  const t = target ?? fallback;
  if (t === 'self') return ectx.source;
  if (t === 'opponent') return other(ectx.source);
  const [face] = drawFresh(ctx, 1);
  return (face ?? 1) % 2 === 0 ? other(ectx.source) : ectx.source;
}

/** §11 recursive cards (ADR-0015 §17): roll `count` fresh d6 off the engine RNG
 * (not the turn pool). `perFace` runs its matching branches per face with
 * `faceValue` in scope; `onSum` runs once with the summed faces (`faceSum`). The
 * sub-effects may not contain `rollFresh` (depth-1). */
function resolveRollFresh(
  ctx: Ctx,
  ectx: EffectCtx,
  count: number,
  perFace: readonly PerFaceEffect[] | undefined,
  onSum: readonly CardEffect[] | undefined,
): ActionTags {
  const tags: ActionTags = {
    attacked: false,
    blocked: false,
    statuses: [],
    damageToOpponent: 0,
    blockGained: 0,
  };
  const faces = drawFresh(ctx, count);
  // HEX-88: a fizzle is a fresh roll that resolves to nothing — no `perFace`
  // branch matched any rolled face and there is no `onSum` block. Pre-computed
  // off the same pure predicate the effect loop uses (no state mutation), so the
  // roll line can carry the flag without changing resolution order.
  const anyBranchMatched =
    perFace !== undefined &&
    faces.some((face) => perFace.some((branch) => matches(face, branch.facePredicate)));
  const fizzled = onSum === undefined && !anyBranchMatched;
  log(ctx, { kind: 'rollFresh', side: ectx.source, faces, fizzled });
  if (perFace !== undefined) {
    for (const face of faces) {
      if (ended(ctx)) break;
      for (const branch of perFace) {
        if (matches(face, branch.facePredicate)) {
          mergeTags(
            tags,
            resolveEffects(ctx, branch.effects, {
              ...ectx,
              allowRollFresh: false,
              faceValue: face,
            }),
          );
          if (ended(ctx)) break;
        }
      }
    }
  }
  if (onSum !== undefined && !ended(ctx)) {
    const faceSum = faces.reduce((sum, f) => sum + f, 0);
    mergeTags(tags, resolveEffects(ctx, onSum, { ...ectx, allowRollFresh: false, faceSum }));
  }
  return tags;
}

function doRerollDice(ctx: Ctx, source: Side, count: number): void {
  // Player-targeted reroll (ADR-0015 §19 Reroll): if the activation named a die,
  // reroll exactly that free die. The player picks which bad die to retry.
  const target = ctx.rerollTarget;
  if (target !== undefined) {
    const slot = ctx.state.pool[target];
    if (slot === undefined || slot.assignedTo !== null) return;
    const [face] = drawFresh(ctx, 1);
    const pool = ctx.state.pool.map((s) =>
      s.index === target ? { ...s, face: face ?? s.face } : s,
    );
    ctx.state = { ...ctx.state, pool };
    log(ctx, { kind: 'reroll', side: source, count: 1 });
    return;
  }
  // Untargeted: reroll up to `count` of the active side's still-free dice in
  // index order (recursive-card rerolls and the no-pick activation fallback).
  let rerolled = 0;
  const pool = ctx.state.pool.map((slot) => {
    if (rerolled >= count || slot.assignedTo !== null) return slot;
    rerolled += 1;
    const [face] = drawFresh(ctx, 1);
    return { ...slot, face: face ?? slot.face };
  });
  ctx.state = { ...ctx.state, pool };
  if (rerolled > 0) log(ctx, { kind: 'reroll', side: source, count: rerolled });
}

// --------------------------------------------------------------------------
// Traps (§3, §11.4)
// --------------------------------------------------------------------------

/** Feed assigned dice to a loaded / charged trap on the active side (§3, §17).
 * Skipped for passive traps, dice-less traps, and charged traps that charge off
 * their own trigger (`chargeSource: 'trigger'`, e.g. Doomsday Clock) rather than
 * fed dice. */
function feedTrapIfAssigned(ctx: Ctx, card: Card): void {
  const trap = card.trap;
  if (trap === undefined || trap.variant === 'passive') return;
  if (card.cost.length === 0) return;
  if (trap.variant === 'charged' && (trap.chargeSource ?? 'fedDie') === 'trigger') return;
  if (!isAssignmentReady(ctx.state, card)) return;

  const amount = armOrChargeAmount(card);
  const runtime = trapRuntime(ctx.state, ctx.active, card.id);
  if (trap.variant === 'loaded') {
    setTrapRuntime(ctx, ctx.active, card.id, { ...runtime, armed: true });
    log(ctx, { kind: 'trapArmed', side: ctx.active, cardId: card.id });
  } else {
    const cap = trap.chargeMax ?? Number.POSITIVE_INFINITY;
    const charges = Math.min(cap, runtime.charges + amount);
    setTrapRuntime(ctx, ctx.active, card.id, { ...runtime, charges });
    log(ctx, { kind: 'trapCharged', side: ctx.active, cardId: card.id, charges });
  }
}

function fireRollFaceTraps(ctx: Ctx): void {
  const faces = new Set(ctx.state.pool.map((s) => s.face));
  fireMatchingTraps(ctx, emptyTrigger(ctx.active), (cond) => {
    return cond.kind === 'opponentRollFace' && faces.has(cond.value as DieFace);
  });
}

function fireReactiveTraps(ctx: Ctx, tags: ActionTags): void {
  const trigger: TrapTrigger = {
    attacker: ctx.active,
    damageToOwner: tags.damageToOpponent,
    blockGained: tags.blockGained,
    statuses: tags.statuses,
  };
  fireMatchingTraps(ctx, trigger, (cond) => {
    switch (cond.kind) {
      case 'opponentAttacks':
        return tags.attacked;
      case 'opponentBlocks':
        return tags.blocked;
      case 'opponentAppliesStatus':
        return tags.statuses.some((s) => cond.status === undefined || s.status === cond.status);
      default:
        return false;
    }
  });
}

function fireTurnEndTraps(ctx: Ctx): void {
  // Fire active side's turnEnd traps first, then the opponent's (§11.4). The
  // trigger payload is empty — turnEnd traps (Doomsday Clock) read no magnitude.
  fireMatchingTrapsFor(ctx, ctx.active, emptyTrigger(other(ctx.active)), (cond) => {
    return cond.kind === 'turnEnd';
  });
  if (!ended(ctx)) {
    fireMatchingTrapsFor(ctx, other(ctx.active), emptyTrigger(ctx.active), (cond) => {
      return cond.kind === 'turnEnd';
    });
  }
}

/** Reactive traps belong to the opponent of the active side (they react to the
 * active side's actions). */
function fireMatchingTraps(
  ctx: Ctx,
  trigger: TrapTrigger,
  match: (cond: TrapCondition) => boolean,
): void {
  fireMatchingTrapsFor(ctx, other(ctx.active), trigger, match);
}

function fireMatchingTrapsFor(
  ctx: Ctx,
  ownerSide: Side,
  trigger: TrapTrigger,
  match: (cond: TrapCondition) => boolean,
): void {
  if (ctx.inTrap) return; // depth-1: a trap cannot trigger another trap
  const owner = combatantOf(ctx.state, ownerSide);
  for (const placement of rowMajor(owner.inventory.loadout.placements)) {
    if (ended(ctx)) break;
    const card = placement.card;
    if (card.type !== 'trap' || card.trap === undefined) continue;
    if (!match(card.trap.trigger)) continue;
    fireTrap(ctx, ownerSide, card, trigger);
  }
}

function fireTrap(ctx: Ctx, ownerSide: Side, card: Card, trigger: TrapTrigger): void {
  const trap = card.trap;
  if (trap === undefined) return;

  // §17 per-turn fire cap (Mirror = 1).
  if (
    trap.maxFiresPerTurn !== undefined &&
    (ctx.trapFires.get(card.id) ?? 0) >= trap.maxFiresPerTurn
  ) {
    return;
  }

  const runtime = trapRuntime(ctx.state, ownerSide, card.id);

  if (trap.variant === 'loaded') {
    if (!runtime.armed) return;
    setTrapRuntime(ctx, ownerSide, card.id, { ...runtime, armed: false });
  } else if (trap.variant === 'charged') {
    const chargeSource = trap.chargeSource ?? 'fedDie';
    if (chargeSource === 'trigger') {
      // Each trigger adds a charge (ADR-0015 §17). `atChargeMax` fires only when
      // the cap is hit, then resets; `onTrigger` fires on every trigger.
      const cap = trap.chargeMax ?? Number.POSITIVE_INFINITY;
      const charges = Math.min(cap, runtime.charges + 1);
      if ((trap.fireMode ?? 'onTrigger') === 'atChargeMax') {
        if (charges < cap) {
          setTrapRuntime(ctx, ownerSide, card.id, { ...runtime, charges });
          log(ctx, { kind: 'trapCharged', side: ownerSide, cardId: card.id, charges });
          return;
        }
        setTrapRuntime(ctx, ownerSide, card.id, { ...runtime, charges: 0 });
      } else {
        setTrapRuntime(ctx, ownerSide, card.id, { ...runtime, charges });
      }
    } else {
      // fedDie: consume one stored charge per trigger (Spike Wall).
      if (runtime.charges <= 0) return;
      setTrapRuntime(ctx, ownerSide, card.id, { ...runtime, charges: runtime.charges - 1 });
    }
  }

  log(ctx, { kind: 'trapFired', side: ownerSide, cardId: card.id, cardName: card.name });
  if (trap.maxFiresPerTurn !== undefined) {
    ctx.trapFires.set(card.id, (ctx.trapFires.get(card.id) ?? 0) + 1);
  }

  // Depth-1: suppress reactive trap firing while a trap's own effects resolve.
  const prev = ctx.inTrap;
  ctx.inTrap = true;
  resolveEffects(ctx, triggerEffects(card), { source: ownerSide, allowRollFresh: true, trigger });
  ctx.inTrap = prev;
}

// --------------------------------------------------------------------------
// Effect primitives
// --------------------------------------------------------------------------

/** Apply `amount` damage from `attacker` to `target`, returning the amount dealt
 * (after weaken scaling, before block). `raw` damage — self-cost (Bloodletting),
 * self-target (Roulette odd) and reflections (Caltrops) — skips weaken scaling
 * and the target's block: direct HP loss (ADR-0015 §14, §17). */
function applyDamage(
  ctx: Ctx,
  attacker: Side,
  target: Side,
  amount: number,
  opts: { raw: boolean },
): number {
  const targetC = combatantOf(ctx.state, target);
  if (targetC.hp <= 0) return 0;
  const scaled = opts.raw
    ? amount
    : scaleOutgoingDamage(amount, combatantOf(ctx.state, attacker).statusBag);
  const blocked = opts.raw ? 0 : Math.min(targetC.block, scaled);
  const hpAfter = Math.max(0, targetC.hp - (scaled - blocked));
  mutate(ctx, target, { block: targetC.block - blocked, hp: hpAfter });
  log(ctx, { kind: 'damage', source: attacker, target, amount: scaled, blocked, hpAfter });
  checkDeath(ctx);
  return scaled;
}

/** Restore `amount` HP to `side`, clamped at maxHp (HEX-89). A dead side stays
 * dead — heal can't revive a 0-HP combatant. Logs the HP actually gained. */
function applyHeal(ctx: Ctx, side: Side, amount: number): void {
  const c = combatantOf(ctx.state, side);
  if (c.hp <= 0 || amount <= 0) return;
  const hpAfter = Math.min(c.maxHp, c.hp + amount);
  mutate(ctx, side, { hp: hpAfter });
  log(ctx, { kind: 'heal', side, amount: hpAfter - c.hp, hpAfter });
}

function addBlock(ctx: Ctx, side: Side, amount: number): number {
  const c = combatantOf(ctx.state, side);
  const total = c.block + amount;
  mutate(ctx, side, { block: total });
  return total;
}

/** Apply a status to a concrete side. Stored self-relative so HEX-20's overwrite
 * key (status, target) stays consistent per bearer. */
function applyStatusToSide(ctx: Ctx, targetSide: Side, app: StatusApplication): void {
  const targetC = combatantOf(ctx.state, targetSide);
  if (targetC.hp <= 0) return;
  const bearerApp: StatusApplication = { ...app, target: 'self' };
  const bag = applyStatus(targetC.statusBag, bearerApp);
  mutate(ctx, targetSide, { statusBag: bag });
  log(ctx, { kind: 'statusApplied', target: targetSide, status: app });
}

/** Freeze one of `side`'s cards for one turn (ADR-0015 §17 freezeCard). The
 * `highestCostPlayable` selector picks the dice-playable card (non-trap,
 * non-die, not already frozen) with the largest cost, ties broken row-major. */
function freezeSelectedCard(ctx: Ctx, side: Side, select: 'highestCostPlayable'): void {
  void select; // only one selector in V1
  const c = combatantOf(ctx.state, side);
  let chosen: Card | undefined;
  for (const placement of rowMajor(c.inventory.loadout.placements)) {
    const card = placement.card;
    if (card.type === 'trap' || card.cost.length === 0) continue;
    if ((c.frozenCards.get(card.id) ?? 0) > 0) continue;
    if (chosen === undefined || card.cost.length > chosen.cost.length) chosen = card;
  }
  if (chosen === undefined) return;
  const frozen = new Map(c.frozenCards);
  frozen.set(chosen.id, 1);
  mutate(ctx, side, { frozenCards: frozen });
  log(ctx, { kind: 'cardFrozen', side, cardId: chosen.id, cardName: chosen.name });
}

/** §11.5 active-side tick: tick → route poison to HP → expire. No drain step is
 * needed — this engine applies statuses immediately (not buffered) because the
 * turn-by-turn model never re-rolls inside a turn, so there is no Weaken-same-
 * turn hazard the V1 buffering guarded against. */
function tickActive(ctx: Ctx): void {
  const side = ctx.active;
  const c = combatantOf(ctx.state, side);
  if (c.hp <= 0) return;
  const t = tick(c.statusBag);
  const hpAfter = Math.max(0, c.hp - t.ownerDamage);
  mutate(ctx, side, {
    hp: hpAfter,
    statusBag: expire(t.bag),
    frozenCards: decrementFrozen(c.frozenCards),
  });
  if (t.ownerDamage > 0) {
    log(ctx, { kind: 'poisonTick', side, amount: t.ownerDamage, hpAfter });
  }
  checkDeath(ctx);
}

/** Tick down frozen cards on the owner's turn end (ADR-0015 §17); drop entries
 * that hit 0. */
function decrementFrozen(frozen: ReadonlyMap<CardId, number>): Map<CardId, number> {
  const next = new Map<CardId, number>();
  for (const [id, turns] of frozen) {
    if (turns > 1) next.set(id, turns - 1);
  }
  return next;
}

function scaleOutgoingDamage(amount: number, attackerBag: StatusBag): number {
  const weaken = query(attackerBag, 'weaken');
  if (weaken <= 0) return amount;
  return Math.max(0, amount - weaken);
}

/** §11.6 — first combatant to reach 0 HP loses at that instant. */
function checkDeath(ctx: Ctx): void {
  if (ended(ctx)) return;
  const playerDead = ctx.state.player.hp <= 0;
  const enemyDead = ctx.state.enemy.hp <= 0;
  if (!playerDead && !enemyDead) return;
  // Whoever hit 0 first loses. If both are somehow 0 in one mutation step, the
  // damage source survives semantics don't apply (single mutation hits one
  // side); resolve a tie in the player's favour, matching the V1 rule.
  const victor: Side = playerDead && !enemyDead ? 'enemy' : 'player';
  ctx.state = { ...ctx.state, phase: 'ended', victor };
  log(ctx, { kind: 'combatEnded', victor });
}

// --------------------------------------------------------------------------
// Enemy AI (ADR-0016 / HEX-61) — tier-gated selector in `enemyAi.ts`
// --------------------------------------------------------------------------

/** Run the tier-gated selector (`chooseEnemyAssignment`, ADR-0016) and commit its
 * chosen assignments through the engine's own `doAssign` path, so every proposed
 * die is re-validated by the cost predicates. The selector is pure over the state;
 * resolution (recursion, reactive traps, depth-1 caps) stays exactly as before. */
function applyEnemyAssignment(state: CardCombatState): CardCombatState {
  let s = state;
  for (const assignment of chooseEnemyAssignment(s)) {
    for (const dieIndex of assignment.dieIndices) {
      s = doAssign(s, dieIndex, assignment.cardId);
    }
  }
  return s;
}

// --------------------------------------------------------------------------
// Dice & RNG (replayed from seed + step count — keeps state pure data)
// --------------------------------------------------------------------------

function rollForActive(state: CardCombatState): CardCombatState {
  const active = combatantOf(state, state.activeSide);
  // The active side's pool is rolled through its OWNED dice (ADR-0018 §3) — a
  // specialist die guarantees its faces; a plain-d6 pool is byte-identical to the
  // pre-HEX-116 fixed-d6 roll (`rollDie(rng, PLAIN_D6)` === `1 + floor(rng*6)`).
  const { faces, nextStepCount } = rollFaces(state.seed, state.rngStepCount, poolDice(active));
  const pool: DieSlot[] = faces.map((face, index) => ({ index, face, assignedTo: null }));
  const next: CardCombatState = {
    ...state,
    rngStepCount: nextStepCount,
    pool,
    assignments: [],
  };
  // Mirror the rolled faces into the active combatant's schema field, and clear
  // the active side's per-turn fire caps at the start of its turn (ADR-0015 §19).
  const synced = withCombatant(next, state.activeSide, {
    dicePool: [...faces],
    oncePerTurnFired: new Set(),
  });
  const logged: CardCombatState = {
    ...synced,
    log: [...synced.log, { kind: 'turnStart', side: state.activeSide, turn: state.turn, faces }],
  };

  // §11.4 (roll-face traps) — the opponent's `opponentRollFace` traps react to
  // the freshly rolled pool now, before the active side assigns, so a Cold Snap
  // freeze can bar a card from receiving a die this turn (ADR-0015 §14, §17).
  const ctx: Ctx = { state: logged, active: state.activeSide, inTrap: false, trapFires: new Map() };
  fireRollFaceTraps(ctx);
  return ctx.state;
}

/** Draw `count` fresh plain d6 off the engine stream (for rollFresh / reroll),
 * advancing the step count on `ctx.state`. Fresh dice are always plain d6 —
 * recursive rolls draw off the engine RNG, not the owner's owned dice
 * (ADR-0015 §11). */
function drawFresh(ctx: Ctx, count: number): DieFace[] {
  const { faces, nextStepCount } = rollFaces(
    ctx.state.seed,
    ctx.state.rngStepCount,
    plainDice(count),
  );
  ctx.state = { ...ctx.state, rngStepCount: nextStepCount };
  return faces;
}

/** Replay the seeded stream to `stepCount`, then roll each of `dice` once (one
 * `rng()` draw per die). An all-`PLAIN_D6` pool reproduces the historic
 * `1 + floor(rng()*6)` sequence exactly, so step count + faces are unchanged for
 * a default combatant. */
function rollFaces(
  seed: number,
  stepCount: number,
  dice: readonly DieType[],
): { faces: DieFace[]; nextStepCount: number } {
  const rng = createRng(seed);
  for (let i = 0; i < stepCount; i++) rng();
  const faces = dice.map((die) => rollDie(rng, die));
  return { faces, nextStepCount: stepCount + dice.length };
}

// --------------------------------------------------------------------------
// Cost / matching helpers
// --------------------------------------------------------------------------

/** The dice a card consumes when the active side plays / feeds it. For loaded /
 * charged traps this is the arm / recharge feed cost; for passive traps and die
 * cards it is empty (skipped in the assignment phase, ADR-0015 §15). */
function feedCost(card: Card): readonly Predicate[] {
  return card.cost;
}

function triggerEffects(card: Card): CardEffect[] {
  return card.effects.filter((e) => e.kind !== 'armOrCharge');
}

function armOrChargeAmount(card: Card): number {
  for (const e of card.effects) {
    if (e.kind === 'armOrCharge') return e.amount;
  }
  return 1;
}

function isAssignmentReady(state: CardCombatState, card: Card): boolean {
  return facesReady(assignedFaces(state, card.id), card);
}

/**
 * Whether a multiset of `faces` is a valid (partial or full) assignment for
 * `card` (ADR-0015 §19). Below the cost-slot count the usual saturating cost
 * matching applies (`hasMatching`); above it, `extraCost` must be present and the
 * faces must split into a full cost matching plus extras that each satisfy
 * `extraCost.predicate`. Cost is always paid before extras stack.
 */
function assignmentValid(faces: readonly DieFace[], card: Card): boolean {
  if (faces.length <= card.cost.length) return hasMatching(faces, card.cost);
  const extra = card.extraCost;
  if (extra === undefined) return false;
  return existsCostPlusExtra(faces, card.cost, extra.predicate);
}

/** Backtracking split: can `faces` cover every cost predicate one-to-one while
 * every leftover face matches `extraPred`? */
function existsCostPlusExtra(
  faces: readonly DieFace[],
  cost: readonly Predicate[],
  extraPred: Predicate,
): boolean {
  const used = new Array<boolean>(faces.length).fill(false);
  const matchCost = (ci: number): boolean => {
    if (ci === cost.length) {
      for (let i = 0; i < faces.length; i++) {
        const face = faces[i];
        if (!used[i] && face !== undefined && !matches(face, extraPred)) return false;
      }
      return true;
    }
    const pred = cost[ci];
    if (pred === undefined) return false;
    for (let i = 0; i < faces.length; i++) {
      const face = faces[i];
      if (used[i] || face === undefined || !matches(face, pred)) continue;
      used[i] = true;
      if (matchCost(ci + 1)) return true;
      used[i] = false;
    }
    return false;
  };
  return matchCost(0);
}

/**
 * Split `faces` into the cost dice (one per cost predicate) and the extra dice
 * (ADR-0015 §19). Used at resolution to fire `costBonuses` per cost die and
 * `extraCost.perDie` per extra die. Returns the first split where the cost
 * predicates are saturated one-to-one AND every leftover face satisfies
 * `extraCost.predicate` — the same constraint `existsCostPlusExtra` enforces for
 * validity (HEX-87: the old greedy match could leave a non-conforming face as an
 * extra, e.g. cost [parity:even] + extra [exact:6] on faces [6,4] would put 6 in
 * the cost slot and treat 4 as a §19 extra, mis-firing the leftover constraint).
 * With no `extraCost`, every face is a cost die.
 */
function splitCostExtra(
  faces: readonly DieFace[],
  card: Card,
): { costFaces: DieFace[]; extraFaces: DieFace[] } {
  const cost = card.cost;
  const extraPred = card.extraCost?.predicate;
  const used = new Array<boolean>(faces.length).fill(false);
  const matchCost = (ci: number): boolean => {
    if (ci === cost.length) {
      // Cost saturated — accept only if every leftover satisfies the extra
      // predicate. With no `extraCost` (extraPred undefined) leftovers are not
      // produced here (faces.length <= cost.length at the call sites that omit
      // it), so the loop is vacuously true.
      if (extraPred === undefined) return true;
      for (let i = 0; i < faces.length; i++) {
        const face = faces[i];
        if (!used[i] && face !== undefined && !matches(face, extraPred)) return false;
      }
      return true;
    }
    const pred = cost[ci];
    if (pred === undefined) return false;
    for (let i = 0; i < faces.length; i++) {
      const face = faces[i];
      if (used[i] || face === undefined || !matches(face, pred)) continue;
      used[i] = true;
      if (matchCost(ci + 1)) return true;
      used[i] = false;
    }
    return false;
  };
  matchCost(0);
  const costFaces: DieFace[] = [];
  const extraFaces: DieFace[] = [];
  for (let i = 0; i < faces.length; i++) {
    const face = faces[i];
    if (face === undefined) continue;
    (used[i] ? costFaces : extraFaces).push(face);
  }
  return { costFaces, extraFaces };
}

/** Whether `faces` can be matched one-to-one into distinct `predicates` (a
 * matching saturating all faces exists). Backtracking — `faces`/`predicates`
 * are short (≤ a few). */
function hasMatching(faces: readonly DieFace[], predicates: readonly Predicate[]): boolean {
  if (faces.length > predicates.length) return false;
  const usedPred = new Array<boolean>(predicates.length).fill(false);
  const assign = (i: number): boolean => {
    if (i === faces.length) return true;
    const face = faces[i];
    if (face === undefined) return true;
    for (let p = 0; p < predicates.length; p++) {
      const pred = predicates[p];
      if (usedPred[p] || pred === undefined || !matches(face, pred)) continue;
      usedPred[p] = true;
      if (assign(i + 1)) return true;
      usedPred[p] = false;
    }
    return false;
  };
  return assign(0);
}

// --------------------------------------------------------------------------
// State helpers (pure — every mutation returns a fresh Combatant)
// --------------------------------------------------------------------------

function activeCombatant(state: CardCombatState): Combatant {
  return combatantOf(state, state.activeSide);
}

function combatantOf(state: CardCombatState, side: Side): Combatant {
  return side === 'player' ? state.player : state.enemy;
}

function withCombatant(
  state: CardCombatState,
  side: Side,
  patch: Partial<Combatant>,
): CardCombatState {
  const updated = { ...combatantOf(state, side), ...patch };
  return side === 'player' ? { ...state, player: updated } : { ...state, enemy: updated };
}

function mutate(ctx: Ctx, side: Side, patch: Partial<Combatant>): void {
  ctx.state = withCombatant(ctx.state, side, patch);
}

function setBlock(ctx: Ctx, side: Side, block: number): void {
  mutate(ctx, side, { block });
}

function trapRuntime(state: CardCombatState, side: Side, cardId: CardId): TrapRuntime {
  return combatantOf(state, side).trapState.get(cardId) ?? { armed: false, charges: 0 };
}

function setTrapRuntime(ctx: Ctx, side: Side, cardId: CardId, runtime: TrapRuntime): void {
  const c = combatantOf(ctx.state, side);
  const trapState = new Map(c.trapState);
  trapState.set(cardId, runtime);
  mutate(ctx, side, { trapState });
}

function findLoadout(combatant: Combatant, cardId: CardId): GridPlacement | undefined {
  return combatant.inventory.loadout.placements.find((p) => p.card.id === cardId);
}

function rowMajor(placements: readonly GridPlacement[]): GridPlacement[] {
  return [...placements].sort((a, b) => a.origin.row - b.origin.row || a.origin.col - b.origin.col);
}

function slotFace(state: CardCombatState, index: number): DieFace {
  const slot = state.pool[index];
  if (slot === undefined) throw new Error(`invalid die index ${index}`);
  return slot.face;
}

function log(ctx: Ctx, event: CombatEvent): void {
  ctx.state = { ...ctx.state, log: [...ctx.state.log, event] };
}

function other(side: Side): Side {
  return side === 'player' ? 'enemy' : 'player';
}

function ended(ctx: Ctx): boolean {
  return ctx.state.phase === 'ended';
}

function cloneCombatant(c: Combatant): Combatant {
  return {
    ...c,
    dicePool: [...c.dicePool],
    statusBag: c.statusBag.map((s) => ({ ...s })),
    trapState: new Map([...c.trapState.entries()].map(([k, v]) => [k, { ...v }])),
    frozenCards: new Map(c.frozenCards),
    oncePerCombatFired: new Set(c.oncePerCombatFired),
    oncePerTurnFired: new Set(c.oncePerTurnFired),
    inventory: {
      loadout: {
        placements: c.inventory.loadout.placements.map((p) => ({ ...p })),
        capacity: { ...c.inventory.loadout.capacity },
      },
    },
  };
}
